// BrandLoader — the BigAgri "B" loading mark. A dim B with a bright stroke
// chasing round its outline (the Supabase-dashboard bolt loader, our mark).
// The B is traced from public/icons/icon-512.png in that file's pixel space,
// so it stays the same shape as the home-screen icon.
//
// It draws in the active product's brand colour: `--brand-color`, which
// useBrandChrome sets on <html>; BigAgri green before that (pre-auth, the
// admin app). `color` overrides it. Keyframes live in src/index.css
// (`brand-loader-*`); reduced motion gets a gentle pulse. Inside a wrapper
// that already is the `role="status"`, pass `label={null}` so the loading
// state is announced once. On a white or light-grey page pass
// `surface="light"`: the glow is for the dark shell and reads as a haze on white. index.html carries
// a static copy for the moment before the bundle runs, in the last brand
// colour this browser showed.
//
// LoadingWords is the line under a page-sized loader: a farm or packhouse verb
// that changes every few seconds ("Ploughing…", "Degreening…"), in the manner
// of Claude's "Thinking…" words. It is aria-hidden: the B is already the
// `role="status"`, and a live region changing every 2.5 s would talk over a
// screen reader. ⚠️ No word may be a UI label (a module, tab, stage or status
// name: Tipping, Scouting, Packing…) — the word is real text on the page, so a
// test matching /^Tipping/ found two elements whenever the loader showed it.
// loadingWords.labels.test.ts scans src/ and every test's text matcher.
import { useEffect, useState } from 'react'

export const LOADING_WORDS = [
  'Ploughing', 'Tilling', 'Harrowing', 'Furrowing', 'Sowing', 'Germinating',
  'Sprouting', 'Mulching', 'Composting', 'Irrigating', 'Weeding', 'Hoeing',
  'Ridging', 'Staking', 'Transplanting', 'Grafting', 'Trellising', 'Budding',
  'Blossoming', 'Pollinating', 'Cross-pollinating', 'Photosynthesising',
  'Thinning', 'Topping', 'Deleafing', 'Suckering', 'Girdling', 'Ripening',
  'Gleaning', 'Threshing', 'Winnowing', 'Haymaking', 'Baling', 'Mowing',
  'Ruminating', 'Grazing', 'Shepherding', 'Culling', 'Washing', 'Brushing',
  'Waxing', 'Sizing', 'Grading', 'Hydrocooling', 'Precooling', 'Curing',
  'Chitting', 'Hardening-off', 'Cultivating', 'Tending',
  // The soil
  'Subsoiling', 'Liming', 'Overwintering', 'Rotavating', 'Terracing', 'Fallowing',
  'Cover-cropping', 'Raking', 'Contouring',
  // The plant
  'Rooting', 'Tillering', 'Leafing', 'Blooming', 'Fruiting', 'Colouring',
  'Sweetening', 'Maturing', 'Sun-ripening', 'Basking', 'Unfurling',
  // The orchard
  'Espaliering', 'Topworking', 'Bagging', 'Hedging', 'Skirting', 'Fertigating',
  'Sprinkling', 'Pinching', 'Tying-in',
  // The bees
  'Beekeeping', 'Buzzing', 'Swarming', 'Nectaring',
  // The animals
  'Foraging', 'Burrowing', 'Hatching', 'Nesting', 'Herding', 'Wrangling',
  'Shearing', 'Churning', 'Cud-chewing', 'Moseying',
  // The kitchen
  'Fermenting', 'Pickling', 'Preserving', 'Juicing', 'Zesting', 'Peeling',
  'Squeezing', 'Marmalading', 'Jamming',
  // The shed
  'Punneting', 'Cartoning', 'Tedding', 'Tallying', 'Polishing',
  'Buffing',
  // Just for fun
  'Tractoring', 'Wheelbarrowing', 'Scarecrowing', 'Haystacking', 'Pitchforking',
  'Muck-spreading', 'Barn-raising', 'Rain-dancing',
  'Bean-counting', 'Cloud-watching', 'Dew-gathering', 'Almanac-reading',
  'Weather-watching',
] as const

const WORD_MS = 2500

/** Next word index, never the one on screen now. */
function nextWord(i: number) {
  return (i + 1 + Math.floor(Math.random() * (LOADING_WORDS.length - 1))) % LOADING_WORDS.length
}

export function LoadingWords({ className = 'text-caption text-fg-muted' }: { className?: string }) {
  const [i, setI] = useState(() => Math.floor(Math.random() * LOADING_WORDS.length))
  useEffect(() => {
    const t = setInterval(() => setI(nextWord), WORD_MS)
    return () => clearInterval(t)
  }, [])
  return (
    <p key={i} aria-hidden className={`loading-word ${className}`}>
      {LOADING_WORDS[i]}…
    </p>
  )
}

const OUTLINE =
  'M147 115H335V147H366V240H335V272H366V365H335V397H147Z'
const COUNTERS = 'M179 147H304V240H179Z M179 272H304V365H179Z'

export function BrandLoader({
  className = 'w-12 h-12',
  color,
  label = 'Loading',
  surface = 'shell',
}: { className?: string; color?: string; label?: string | null; surface?: 'shell' | 'light' }) {
  return (
    <svg
      role={label === null ? undefined : 'status'}
      aria-label={label ?? undefined}
      aria-hidden={label === null ? true : undefined}
      viewBox="137 105 239 302"
      className={`brand-loader ${surface === 'light' ? 'brand-loader-light ' : ''}${className}`}
      style={color ? { color } : undefined}
    >
      <path d={`${OUTLINE} ${COUNTERS}`} fillRule="evenodd" fill="currentColor" opacity={0.25} />
      <path
        className="brand-loader-trace"
        d={OUTLINE}
        pathLength={100}
        fill="none"
        stroke="currentColor"
        strokeWidth={14}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

